import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import axiosInstance from "../../../services/server/axios";
import Loader from "../../../components/loader/loader";

const CryptomusCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("checking");
  const [message, setMessage] = useState("");

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get parameters from URL
        const uuid = searchParams.get("uuid");
        const orderId = searchParams.get("order_id");
        const statusParam = searchParams.get("status");
        
        console.log("Cryptomus callback received:", { uuid, orderId, statusParam });

        // Check if we have a pending payment in localStorage
        const pendingPaymentStr = localStorage.getItem("pendingCryptomusPayment");
        let pendingPayment = null;
        
        if (pendingPaymentStr) {
          pendingPayment = JSON.parse(pendingPaymentStr);
          console.log("Found pending payment:", pendingPayment);
        }

        // If we have UUID or orderId, check payment status
        if (uuid || orderId) {
          let response;
          
          if (uuid) {
            response = await axiosInstance.get(`/cryptomus/payment-status/${uuid}`);
          } else if (orderId) {
            response = await axiosInstance.get(`/cryptomus/payment-status-by-order/${orderId}`);
          }
          
          if (response.data.success) {
            const paymentStatus = response.data.status;
            const achatOffreId = response.data.achat_offre_id;
            
            if (paymentStatus === "paid" || paymentStatus === "paid_over") {
              // Payment successful
              setStatus("success");
              setMessage("Payment completed successfully!");
              
              // Clear pending payment
              localStorage.removeItem("pendingCryptomusPayment");
              
              // If we have achatOffreId, navigate to complete purchase
              if (achatOffreId) {
                setTimeout(() => {
                  navigate(`/complete-purchase/${achatOffreId}`);
                }, 2000);
              } else if (pendingPayment?.achatOffreId) {
                setTimeout(() => {
                  navigate(`/complete-purchase/${pendingPayment.achatOffreId}`);
                }, 2000);
              } else {
                // Navigate to dashboard after 3 seconds
                setTimeout(() => {
                  navigate("/dashboard");
                }, 3000);
              }
              
              return;
            } else if (paymentStatus === "cancel" || paymentStatus === "fail") {
              setStatus("failed");
              setMessage("Payment was cancelled or failed.");
              localStorage.removeItem("pendingCryptomusPayment");
              
              setTimeout(() => {
                navigate("/dashboard");
              }, 3000);
              return;
            } else if (paymentStatus === "process" || paymentStatus === "check") {
              setStatus("processing");
              setMessage("Payment is still processing. Please wait...");
              
              // Check again after 5 seconds
              setTimeout(() => {
                handleCallback();
              }, 5000);
              return;
            }
          }
        }
        
        // If we have pending payment but no UUID/orderId in URL
        if (pendingPayment) {
          setStatus("checking_pending");
          setMessage("Checking your payment status...");
          
          // Try to check payment status using stored info
          try {
            let response;
            
            if (pendingPayment.uuid) {
              response = await axiosInstance.get(`/cryptomus/payment-status/${pendingPayment.uuid}`);
            } else if (pendingPayment.orderId) {
              response = await axiosInstance.get(`/cryptomus/payment-status-by-order/${pendingPayment.orderId}`);
            }
            
            if (response.data.success) {
              const paymentStatus = response.data.status;
              
              if (paymentStatus === "paid" || paymentStatus === "paid_over") {
                setStatus("success");
                setMessage("Payment completed successfully!");
                localStorage.removeItem("pendingCryptomusPayment");
                
                if (response.data.achat_offre_id) {
                  setTimeout(() => {
                    navigate(`/complete-purchase/${response.data.achat_offre_id}`);
                  }, 2000);
                } else {
                  setTimeout(() => {
                    navigate("/dashboard");
                  }, 3000);
                }
                return;
              }
            }
          } catch (error) {
            console.error("Error checking pending payment:", error);
          }
        }
        
        // Default fallback
        setStatus("unknown");
        setMessage("Unable to verify payment status. Please check your purchase history.");
        
        setTimeout(() => {
          navigate("/dashboard");
        }, 5000);
        
      } catch (error) {
        console.error("Error in Cryptomus callback:", error);
        setStatus("error");
        setMessage("An error occurred while processing your payment.");
        
        setTimeout(() => {
          navigate("/dashboard");
        }, 5000);
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  const getStatusContent = () => {
    switch (status) {
      case "checking":
      case "checking_pending":
      case "processing":
        return (
          <div className="text-center">
            <Loader />
            <p className="mt-4 text-lg">{message || "Checking payment status..."}</p>
          </div>
        );
      
      case "success":
        return (
          <div className="text-center">
            <div className="text-green-500 text-6xl mb-4">✓</div>
            <h2 className="text-2xl font-bold mb-2">Payment Successful!</h2>
            <p className="text-lg mb-4">{message}</p>
            <p className="text-gray-600">Redirecting to complete your purchase...</p>
          </div>
        );
      
      case "failed":
        return (
          <div className="text-center">
            <div className="text-red-500 text-6xl mb-4">✗</div>
            <h2 className="text-2xl font-bold mb-2">Payment Failed</h2>
            <p className="text-lg mb-4">{message}</p>
            <button
              className="btn btn-primary mt-4"
              onClick={() => navigate("/dashboard")}
            >
              Go to Dashboard
            </button>
          </div>
        );
      
      case "error":
        return (
          <div className="text-center">
            <div className="text-yellow-500 text-6xl mb-4">⚠</div>
            <h2 className="text-2xl font-bold mb-2">Error</h2>
            <p className="text-lg mb-4">{message}</p>
            <button
              className="btn btn-primary mt-4"
              onClick={() => navigate("/dashboard")}
            >
              Go to Dashboard
            </button>
          </div>
        );
      
      default:
        return (
          <div className="text-center">
            <div className="text-gray-500 text-6xl mb-4">?</div>
            <h2 className="text-2xl font-bold mb-2">Payment Status Unknown</h2>
            <p className="text-lg mb-4">{message}</p>
            <p className="text-gray-600">Please check your purchase history in the dashboard.</p>
            <button
              className="btn btn-primary mt-4"
              onClick={() => navigate("/dashboard")}
            >
              Go to Dashboard
            </button>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full p-6 bg-white rounded-lg shadow-lg">
        {getStatusContent()}
      </div>
    </div>
  );
};

export default CryptomusCallback;